/**
 * @fileoverview Client-side search component that handles search suggestions,
 * keyboard navigation, and search UI interactions for the Pothys e-commerce platform.
 */

'use strict';

var debounce = require('lodash/debounce');
var endpoint = $('.suggestions-wrapper').data('url');
var minChars = 3;

// Keyboard navigation constants
var UP_KEY = 38;
var DOWN_KEY = 40;
var DIRECTION_DOWN = 1;
var DIRECTION_UP = -1;

var header = $('.header');

/**
 * Retrieves the suggestions wrapper element relative to the search input
 * @param {HTMLElement} scope - Search input field DOM element
 * @return {jQuery} jQuery object of the .suggestions-wrapper element
 */
function getSuggestionsWrapper(scope) {
    return $(scope).siblings('.suggestions-wrapper');
}

/**
 * Determines if the search is being performed in mobile view
 * @param {HTMLElement} scope - Search input field DOM element
 * @return {boolean} True if search is in mobile view
 */
function isMobileSearch(scope) {
    return !!$(scope).closest('.search-mobile').length;
}

/**
 * Toggles the search icon between search and close states in mobile view
 * @param {string} action - Action to perform ('search' or 'close')
 */
function toggleSuggestionsIcon(action) {
    var mobileSearchIcon = '.search-mobile button.';
    var iconSearch = 'fa-search';
    var iconSearchClose = 'fa-close';

    if (action === 'close') {
        $(mobileSearchIcon + iconSearch).removeClass(iconSearch)
            .addClass(iconSearchClose)
            .attr('type', 'button');
    } else {
        $(mobileSearchIcon + iconSearchClose).removeClass(iconSearchClose)
            .addClass(iconSearch)
            .attr('type', 'submit');
    }
}

/**
 * Manages visibility of "More Content Below" icon based on scroll position
 * @param {HTMLElement} scope - Element to check for scroll position
 */
function handleMoreContentBelowIcon(scope) {
    if (($(scope).scrollTop() + $(scope).innerHeight()) >= $(scope)[0].scrollHeight) {
        $('.more-below').fadeOut();
    } else {
        $('.more-below').fadeIn();
    }
}

/**
 * Positions the suggestions panel on the page, particularly for mobile view
 * @param {HTMLElement} scope - Search input field DOM element
 */
function positionSuggestions(scope) {
    if (isMobileSearch(scope)) {
        var $scope = $(scope);
        var top = $scope.offset().top;
        var outerHeight = $scope.outerHeight();
        var $suggestions = getSuggestionsWrapper(scope).find('.suggestions');

        $suggestions.css('top', top + outerHeight);
        handleMoreContentBelowIcon(scope);

        // Bind scroll event for dynamic "More Below" icon visibility
        $suggestions.scroll(function () {
            handleMoreContentBelowIcon(this);
        });
    }
}

/**
 * Handles modal state for first-time search interaction
 */
function handleSuggestionModal() {
    header.removeClass('search-suggestions-active-m');
    $('.search-bar').addClass('d-none');
}

/**
 * Processes the AJAX response from search suggestions request
 * @param {Object|string} response - Response from the server containing suggestion data
 */
function processResponse(response) {
    var $suggestionsWrapper = getSuggestionsWrapper(this).empty();

    header.addClass('search-suggestions-active-m');
    $('.search-bar').removeClass('d-none');

    if (typeof (response) !== 'object') {
        // Append and show suggestions
        $suggestionsWrapper.append(response).show();

        // Manage visibility of recent searches based on input state
        if ($(this).val().length > 0) {
            $suggestionsWrapper.find('.recent-searches-section').hide();
        } else {
            $suggestionsWrapper.find('.recent-searches-section').show();
        }

        // Show reset button and position suggestions
        $(this).siblings('.reset-button').addClass('d-sm-block');
        positionSuggestions(this);

        // Handle mobile-specific UI updates
        if (isMobileSearch(this)) {
            toggleSuggestionsIcon('close');
        }

        // Update ARIA attributes for screen readers
        var suggestionsList = $('.suggestions .item');
        if ($(suggestionsList).length) {
            $('input.search-field').attr('aria-describedby', 'search-result-count');
        } else {
            $('input.search-field').removeAttr('aria-describedby');
        }
    } else {
        $suggestionsWrapper.hide();
    }
}

/**
 * Shows the offline error message in the suggestions container
 * @param {HTMLElement} scope - Search input field DOM element
 */
function showOfflineError(scope) {
    var $suggestionsWrapper = getSuggestionsWrapper(scope);
    var $suggestions = $suggestionsWrapper.find('.suggestions');

    $suggestions.find('.containertest > *:not(.offline-error)').hide(); // Hide all other content in containertest

    $suggestions.find('.offline-error').removeClass('d-none'); // Show the offline error
    // $suggestions.show();

    positionSuggestions(scope);
}

/**
 * Hides the offline error message
 * @param {HTMLElement} scope - Search input field DOM element
 */
function hideOfflineError(scope) {
    var $suggestionsWrapper = getSuggestionsWrapper(scope);
    var $suggestions = $suggestionsWrapper.find('.suggestions');

    $suggestions.find('.offline-error').addClass('d-none'); // Hide the offline error

    $suggestions.find('.containertest > *:not(.offline-error)').show(); // Show other content if any
}

/**
 * Retrieves search suggestions from the server based on user input
 * @param {HTMLElement} scope - Search input field DOM element
 */
function getSuggestions(scope) {
    if (!navigator.onLine) { // Check internet connectivity first
        showOfflineError(scope);
        return;
    }

    var searchValue = $(scope).val();

    if (searchValue.length >= minChars) {
        // Get suggestions for search term
        $.ajax({
            context: scope,
            url: endpoint + encodeURIComponent(searchValue),
            method: 'GET',
            data: {
                updateRecentSearches: false,
                showRecent: searchValue.length === 0 // Show recent searches only when input is empty
            },
            success: processResponse,
            error: function (status) {
                if (!navigator.onLine || status === 'timeout') {
                    showOfflineError(scope);
                }
            }
        });
    } else {
        // Get only recent searches when input is empty
        if (navigator.onLine) {
            $.ajax({
                context: scope,
                url: endpoint,
                method: 'GET',
                data: {
                    showRecent: true,
                    q: '' // Empty query to get only recent searches
                },
                success: processResponse,
                error: function (status) {
                    if (!navigator.onLine || status === 'timeout') {
                        showOfflineError(scope);
                    }
                }
            });
        } else {
            showOfflineError(scope);
        }
        toggleSuggestionsIcon('search');
        $(scope).siblings('.reset-button').removeClass('d-sm-block');
    }
}

/**
 * Handles keyboard navigation through search suggestions
 * @param {number} direction - Direction of navigation (DIRECTION_UP or DIRECTION_DOWN)
 */
function handleArrow(direction) {
    // Get all suggestion items
    var suggestionsList = $('.suggestions .item');

    if (suggestionsList.filter('.selected').length === 0) {
        // No item selected, select first item
        suggestionsList.first().addClass('selected');
        $('input.search-field').each(function () {
            $(this).attr('aria-activedescendant', suggestionsList.first()[0].id);
        });
    } else {
        // Navigate through items based on direction
        suggestionsList.each(function (index) {
            var idx = index + direction;
            if ($(this).hasClass('selected')) {
                $(this).removeClass('selected');
                $(this).removeAttr('aria-selected');

                if (suggestionsList.eq(idx).length !== 0) {
                    // Select next/previous item
                    suggestionsList.eq(idx).addClass('selected');
                    suggestionsList.eq(idx).attr('aria-selected', true);
                    $(this).removeProp('aria-selected');
                    $('input.search-field').each(function () {
                        $(this).attr('aria-activedescendant', suggestionsList.eq(idx)[0].id);
                    });
                } else {
                    // Wrap to first item if at end
                    suggestionsList.first().addClass('selected');
                    suggestionsList.first().attr('aria-selected', true);
                    $('input.search-field').each(function () {
                        $(this).attr('aria-activedescendant', suggestionsList.first()[0].id);
                    });
                }
                return false;
            }
            return true;
        });
    }
}

module.exports = function () {
    $('form[name="simpleSearch"]').submit(function (e) {
        var suggestionsList = $('.suggestions .item');
        if (suggestionsList.filter('.selected').length !== 0) {
            e.preventDefault();
            suggestionsList.filter('.selected').find('a')[0].click();
        }
        // Update recent searches on form submit
        var searchTerm = $(this).find('input.search-field').val();
        if (searchTerm && searchTerm.length >= minChars) {
            $.ajax({
                url: endpoint + encodeURIComponent(searchTerm),
                method: 'GET',
                success: function (response) {
                    // Process response but don't show suggestions since we're submitting
                    if (typeof (response) === 'object') {
                        // No action needed for object response on form submit
                    }
                }
            });
        }
    });

    $('input.search-field').each(function () {
        /**
         * Use debounce to avoid making an Ajax call on every single key press by waiting a few
         * hundred milliseconds before making the request. Without debounce, the user sees the
         * browser blink with every key press.
         */
        var debounceSuggestions = debounce(function (scope) {
            getSuggestions(scope); // Use the main getSuggestions function instead of duplicating the AJAX call
        }, 300);

        $(this).on('keyup click', function (e) {
            // Capture Down/Up Arrow Key Events
            switch (e.which) {
                case DOWN_KEY:
                    handleArrow(DIRECTION_DOWN);
                    e.preventDefault(); // prevent moving the cursor
                    break;
                case UP_KEY:
                    handleArrow(DIRECTION_UP);
                    e.preventDefault(); // prevent moving the cursor
                    break;
                default:
                    debounceSuggestions(this);
            }
        });
        $(this).on('input', function () {
            var enteredValue = $(this).val().replace(/[^\w ]/g, '').trimStart();
            $(this).val(enteredValue);
        });
    });

    $('body').on('click', function (e) { // hiding modal on click outside
        if (!$('.suggestions').has(e.target).length && !$(e.target).hasClass('search-field')) {
            $('.suggestions').hide();
        }
    });

    $('body').on('click', '.close-search', function () {
        handleSuggestionModal();
    });

    $('.site-search .reset-button').on('click', function () {
        $(this).removeClass('d-sm-block');
    });

    $('.search-mobile').on('click', function (e) {
        e.stopPropagation(); // Prevent click propagation to avoid infinite loop
        if (!$(this).data('clicked')) { // Only trigger if not already triggered to prevent recursion
            $(this).data('clicked', true);
            $('input.search-field').first().trigger('click');
            setTimeout(function () { // Reset the clicked state after a short delay
                $('.search-mobile').data('clicked', false);
            }, 100);
        }
    });

    // Add event listeners for online/offline status
    $(document).ready(function () {
        // Remove the automatic search on coming back online
        window.addEventListener('online', function () {
            // Do nothing - wait for user to click "Try Again"
        });

        // Handle try again button clicks
        $(document).on('click', '.try-again-btn', function (e) {
            e.preventDefault();
            var searchField = $('input.search-field');
            if (navigator.onLine) {
                // Hide the offline error first
                hideOfflineError(searchField[0]);
                // Then continue with search
                getSuggestions(searchField[0]);
            }
        });
    });
};
